import { html, PropertyValueMap, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { BaseComponent } from '../base-component/base-component';
import store from '../../store/store';
import { getAccessCodeDetails } from './thunks/access-code.thunk';
import { clearAccessCodeState } from './store/access-code.store';
import emit from '../../common/events';

@customElement('mhecomm-access-code-lookup')
export class AccessCodeLookup extends connect(store)(BaseComponent) {
  @property()
  label = 'Access Code';

  @property()
  hintText = 'Five sets of four alphanumeric characters separated by hyphens.';

  @property()
  buttonText = 'Check Status';

  @property()
  buttonComponentId = '';

  @property()
  errorMessage = 'Access code is required.';

  @property()
  placeholderText = 'E.g. XX12-XY34-NO99-1Z1Z-BH99';

  @state()
  private inputValue = '';

  @state()
  private validationError = '';

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) {
      this.inputValue = code;
      this.submit(code);
    }
  }

  renderComp(): TemplateResult {
    const inputId = this.buttonComponentId ? `${this.buttonComponentId}-input` : 'access-code-input';
    const hintId = this.buttonComponentId ? `${this.buttonComponentId}-hint` : 'access-code-hint';
    const errorId = this.buttonComponentId ? `${this.buttonComponentId}-error` : 'access-code-error';
    const describedBy = this.validationError ? `${hintId} ${errorId}` : hintId;

    return html`
      <div class="mb-0">
        <label class="form-label fw-semibold mb-0 pb-3" for=${inputId}>
          ${this.label} <span class="fw-normal fst-italic text-muted">*(Required)</span>
        </label>
        <div id=${hintId} class="form-text mt-0 mb-2">${this.hintText}</div>
      </div>
      <div class="mb-3">
        <input
          id=${inputId}
          class="form-control"
          type="text"
          .value=${this.inputValue}
          placeholder=${this.placeholderText}
          @keypress=${this._keyPress}
          @input=${(event: Event) => this.handleInput(event)}
          aria-describedby=${describedBy}
          aria-invalid=${this.validationError ? 'true' : 'false'}
        />
        ${this.validationError
          ? html`<div id=${errorId} class="text-danger mt-2" role="alert" aria-live="assertive">${this.validationError}</div>`
          : ''}
      </div>
      <button
        id=${this.buttonComponentId || 'access-code-lookup-button'}
        class="btn btn-primary mt-2"
        type="button"
        @click=${() => this.handleSubmit()}
      >
        ${this.buttonText}
      </button>
    `;
  }

  private handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.inputValue = value;
    this.validationError = '';
  }

  private handleSubmit() {
    this.submit(this.inputValue);
    emit( {
      element: this,
      name:'access-code-lookup-button-click',
      detail: { 
        accessCode: this.inputValue
      }
    });
  }

  private _keyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  private submit(code: string) {
    const trimmedCode = code.trim();
    const normalizedCode = trimmedCode.replace(/-/g, '');

    const params = new URLSearchParams(window.location.search);
    params.set('code', normalizedCode);
    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.pushState({}, '', newUrl);

    if (!this.isValidCode(trimmedCode)) {
      this.validationError = this.errorMessage;
      store.dispatch(clearAccessCodeState());
      emit( {
        element: this,
        name:'access-code-lookup-validation-error',
        detail: { 
          accessCode: this.inputValue,
          errorMessage: this.validationError
        }
      });
      return;
    }

    this.validationError = '';
    this.inputValue = trimmedCode;

    store.dispatch(getAccessCodeDetails(normalizedCode));
  }

  private isValidCode(code: string): boolean {
    // Access codes are expected to be 20 alphanumeric characters (hyphens are allowed in user input).
    const normalized = code.replace(/-/g, '');
    const hasValidCharacters = /^[a-zA-Z0-9]+$/.test(normalized);
    return hasValidCharacters && normalized.length === 20;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-access-code-lookup': AccessCodeLookup;
  }
}

